import {
  API_BASE_URL,
  API_ENDPOINTS,
  PYTHON_API_BASE_URL,
} from "../../../services/api/apiConstants";
import { simulationService } from "../../../services/api/simulationService";
import { sysConfigApi } from "../../../services/api/sysconfigService";
import { uploadFileApi } from "../../../services/api/inputService";

function buildHeaders({ accessToken, userId }) {
  return {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
    "X-User-ID": userId || "",
  };
}

export async function fetchStorageAccountName({ accessToken, branchId, userId }) {
  const url = `${PYTHON_API_BASE_URL}/storage-account/?branch_id=${branchId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders({ accessToken, userId }),
  });

  if (!response.ok) {
    const error = new Error(`Storage account fetch failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  return data.storage_account.storage_account_name;
}

export async function fetchMicrogridList({ storageAccountName, accessToken }) {
  return simulationService.getList({ customerName: storageAccountName, accessToken });
}

export async function fetchMicrogridSysconfig({ storageAccountName, simulationName, accessToken }) {
  const datasetName = `${simulationName}-sysconfig.json`;
  return sysConfigApi({
    accountname: storageAccountName,
    simulationName,
    datasetName,
    token: accessToken,
  });
}

export async function saveMicrogridSysconfig({ storageAccountName, simulationName, accessToken, payload }) {
  const datasetName = `${simulationName}-sysconfig.json`;
  return sysConfigApi({
    accountname: storageAccountName,
    simulationName,
    datasetName,
    token: accessToken,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Delete a whole microgrid. A microgrid is one storage container in the sim
// (there is no admin record), and the sim's delete_microgrid endpoint
// removes that container with everything in it: inputs, sysconfig and the
// results of every simulation run. Throws an Error carrying the HTTP
// `status` and the sim's `message` on any non-2xx answer.
export async function deleteMicrogrid({ storageAccountName, microgridName, accessToken, userId }) {
  if (!storageAccountName || !microgridName) {
    throw new Error("storage_account and microgrid_name are required to delete a microgrid.");
  }

  const url =
    `${API_BASE_URL}${API_ENDPOINTS.DELETE_MICROGRID}` +
    `?storage_account=${encodeURIComponent(storageAccountName)}` +
    `&microgrid_name=${encodeURIComponent(microgridName)}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      // The sim refuses any non-GET storage request without this header.
      "x-output-storage-manager-type":
        process.env.REACT_APP_UPLOAD_FILES_STORAGE_MANAGER_TYPE || "AzureStorageManager",
      "X-User-ID": userId || sessionStorage.getItem("UserID") || "",
    },
  });

  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    const error = new Error(body?.message || `Microgrid delete failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return body;
}

export async function uploadTariffCsv({ storageAccountName, simulationName, fileName, accessToken, fileContent }) {
  return uploadFileApi({
    customerName: storageAccountName,
    simulationName,
    datasetName: encodeURIComponent(fileName),
    token: accessToken,
    fileContent,
  });
}

export async function parseCsvMetadata({ accessToken, userId, file, fileType }) {
  const formData = new FormData();
  formData.append("csv_file", file);

  const response = await fetch(
    `${PYTHON_API_BASE_URL}/csv-parse-metadata/?file_type=${fileType}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "X-User-ID": userId || "",
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to parse CSV file.");
  }

  return response.json();
}
